/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import {
    FeatureFlagDto,
    GetTasksResponseDto,
    GetUsersResponseDto,
    JobDto,
    LaunchSummaryDto,
    ProductDto,
    ProductRunDto,
    SubscriptionDto,
    SystemInfoDto,
    UserDto,
    UserInfoDto,
} from '@/commons/dtos';
import * as schemas from '@/commons/schemas';
import { axiosBaseQuery } from '@/store/baseQueries';
import { createApi } from '@reduxjs/toolkit/query/react';

export const freApi = createApi({
    reducerPath: 'freApi',
    baseQuery: axiosBaseQuery,
    tagTypes: ['User', 'Product', 'Job', 'Task', 'Subscription', 'SystemInfo', 'FeatureFlag'],
    endpoints: builder => ({
        getUserInfo: builder.query<UserInfoDto, void>({
            query: () => ({ url: '/userInfo', method: 'GET' }),
            transformResponse: response => schemas.UserInfoDtoSchema.parse(response),
        }),
        getUsers: builder.query<GetUsersResponseDto, string>({
            query: (runId: string) => ({ url: '/user/', method: 'GET', params: { runId } }),
            providesTags: ['User'],
            transformResponse: response => schemas.GetUsersResponseDtoSchema.parse(response),
        }),
        deleteUser: builder.mutation<void, string>({
            query: (userId: string) => ({ url: `/user/${userId}`, method: 'DELETE' }),
        }),
        createUsers: builder.mutation({
            query: (users: UserDto[]) => ({ url: '/user/', method: 'POST', data: users }),
            invalidatesTags: ['User'],
        }),
        getLaunchSummary: builder.query<LaunchSummaryDto, string>({
            query: (runId: string) => ({ url: `/launchsummary/${runId}`, method: 'GET' }),
            transformResponse: response => schemas.LaunchSummaryDtoSchema.parse(response),
        }),
        getProductByFlowId: builder.query<ProductDto, string>({
            query: (flowId: string) => ({ url: `/products/flow/${flowId}`, method: 'GET' }),
            transformResponse: response => schemas.ProductDtoSchema.parse(response),
            providesTags: (_, __, flowId) => [{ type: 'Product', flowId }],
        }),
        getProductRunByUserIdAndFlowId: builder.query<ProductRunDto, { userId: string; flowId: string }>({
            query: ({ userId, flowId }) => ({
                url: `/product-runs/user/${userId}`,
                method: 'GET',
                params: { flowId },
            }),
            transformResponse: response => schemas.ProductRunDtoSchema.parse(response),
        }),
        getJobsByProductRunId: builder.query<JobDto[], string>({
            query: (productRunId: string) => ({ url: `/jobs/product-run/${productRunId}`, method: 'GET' }),
            transformResponse: response => schemas.GetProductRunByUserIdAndFlowIdResponseDtoSchema.parse(response),
        }),
        getSubscriptions: builder.query<SubscriptionDto[], void>({
            query: () => ({ url: '/products/subscriptions', method: 'GET' }),
            providesTags: ['Subscription'],
        }),
        updateJobToFail: builder.mutation<void, { runId: string }>({
            query: ({ runId }) => ({ url: `/jobs/setToFail/${runId}`, method: 'PUT' }),
            invalidatesTags: ['Subscription'],
        }),
        launchAribaSku: builder.mutation<void, { runId: string }>({
            query: data => ({ url: '/launch', method: 'POST', data }),
            transformResponse: response => response,
            transformErrorResponse: (response: { status: string | number; data: any }) => {
                return response.data;
            },
            invalidatesTags: ['Subscription'],
        }),
        launch42kSku: builder.mutation<void, { runId: string }>({
            query: data => ({ url: '/spc/', method: 'POST', data }),
            invalidatesTags: ['Subscription'],
        }),
        launchAribaNexusSku: builder.mutation<void, { runId: string }>({
            query: data => ({ url: '/ariba-nexus/', method: 'POST', data }),
            invalidatesTags: ['Subscription'],
        }),
        getTasks: builder.query<GetTasksResponseDto, { flowId: string; runId: string; stepId?: string }>({
            query: params => ({
                url: `/tasks/`,
                method: 'GET',
                params,
            }),
            transformResponse: response => schemas.GetTasksResponseDtoSchema.parse(response),
        }),
        createProductRun: builder.mutation<ProductRunDto, { flowId: string; sku: number; userInfo: { crm_account_id: string; user_uuid: string } }>({
            query: ({ flowId, sku, userInfo }) => ({
                url: '/product-runs/',
                method: 'POST',
                data: {
                    realm: '',
                    orgId: userInfo.crm_account_id,
                    flowId,
                    userId: userInfo.user_uuid,
                    sku,
                    customerId: userInfo.user_uuid,
                    createdBy: userInfo.user_uuid,
                    updatedBy: userInfo.user_uuid,
                },
            }),
            invalidatesTags: (_, __, { flowId }) => [{ type: 'Product', flowId }, 'Subscription'],
        }),
        getCommodityCodeRunExist: builder.query<boolean, string>({
            query: (runId: string) => ({ url: `/commodity-codes-run/exist/${runId}`, method: 'GET' }),
            transformResponse: response => response as boolean,
        }),

        getSystemInfo: builder.query<SystemInfoDto, string>({
            providesTags: ['SystemInfo'],
            query: (key: string) => ({ url: `/system-info/`, method: 'GET', params: { key } }),
        }),
        getFeatureFlags: builder.query<{ [key: string]: FeatureFlagDto }, string[]>({
            providesTags: ['FeatureFlag'],
            query: (featureFlags: string[]) => ({
                url: '/feature-flag/',
                method: 'GET',
                params: { flag: featureFlags.join(',') },
            }),
        }),
    }),
});

export const {
    useGetUserInfoQuery,
    useLazyGetUserInfoQuery,
    useGetUsersQuery,
    useLazyGetUsersQuery,
    useDeleteUserMutation,
    useCreateUsersMutation,
    useGetLaunchSummaryQuery,
    useLazyGetLaunchSummaryQuery,
    useGetProductByFlowIdQuery,
    useGetProductRunByUserIdAndFlowIdQuery,
    useGetJobsByProductRunIdQuery,
    useGetSubscriptionsQuery,
    useUpdateJobToFailMutation,
    useLaunchAribaSkuMutation,
    useLaunch42kSkuMutation,
    useLaunchAribaNexusSkuMutation,
    useGetTasksQuery,
    useCreateProductRunMutation,
    useGetCommodityCodeRunExistQuery,
    useGetSystemInfoQuery,
    useGetFeatureFlagsQuery,
} = freApi;
